import { useEffect, useState } from "react";

// Context-Cache: einmal pro Session laden
let cachedConfig = null;
let pendingPromise = null;
const listeners = new Set();

const DEFAULT_CONFIG = {
  background_url: null,
  brightness: 0.7,
  overlay_opacity: 0.35,
  color_filter: "none",
  blur_px: 0,
  card_glass: true,
  text_contrast: 0.6,
};

// Premium-Default-Hintergrund (dezent, dunkel, industriell) — wird gerendert
// wenn der User kein eigenes Bild hochgeladen hat. Hochwertiger Eindruck
// out-of-the-box ohne den Gradient-Fallback.
const PREMIUM_DEFAULT_BG = "https://images.unsplash.com/photo-1545153996-22c7e0625d4d?w=2400&q=80&auto=format&fit=crop";
// (Architectural concrete + steel, sehr dunkel — Lizenz: Unsplash)

async function loadDesign() {
  if (cachedConfig) return cachedConfig;
  if (pendingPromise) return pendingPromise;
  pendingPromise = (async () => {
    try {
      const res = await fetch("/api/app-design", {
        headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
      });
      if (res.ok) {
        const data = await res.json();
        cachedConfig = data?.config || DEFAULT_CONFIG;
      } else {
        cachedConfig = DEFAULT_CONFIG;
      }
    } catch {
      cachedConfig = DEFAULT_CONFIG;
    }
    listeners.forEach((l) => l(cachedConfig));
    return cachedConfig;
  })();
  return pendingPromise;
}

export function invalidateAppDesign(newCfg) {
  cachedConfig = newCfg || null;
  pendingPromise = null;
  if (newCfg) listeners.forEach((l) => l(newCfg));
}

export function useAppDesign() {
  const [cfg, setCfg] = useState(cachedConfig || DEFAULT_CONFIG);
  useEffect(() => {
    let mounted = true;
    const onChange = (c) => { if (mounted) setCfg(c); };
    listeners.add(onChange);
    loadDesign().then((c) => { if (mounted) setCfg(c); });
    return () => { mounted = false; listeners.delete(onChange); };
  }, []);
  return cfg;
}

const FILTER_CSS = {
  none: "",
  blue: "hue-rotate(-20deg) saturate(1.1)",
  warm: "sepia(0.2) saturate(1.15)",
  grey: "grayscale(0.6)",
};

/**
 * AppBackground — Fixer, globaler Hintergrund unter allen Views.
 * Position fixed, z-index -1, kein Scrollen.
 */
export default function AppBackground() {
  const cfg = useAppDesign();
  // Mobile/Desktop-Variante automatisch wählen (responsive)
  const isDesktop = typeof window !== "undefined" && window.matchMedia("(min-width: 768px)").matches;
  const userImage = (isDesktop
    ? (cfg?.background_desktop_url || cfg?.background_url)
    : (cfg?.background_mobile_url || cfg?.background_url));
  // Premium-Fallback wenn kein User-Bild gesetzt → dezentes dunkles Default-Bild
  const effectiveImage = userImage || PREMIUM_DEFAULT_BG;
  const hasImage = !!effectiveImage;
  const usingDefault = !userImage; // Indicator
  const brightness = cfg?.brightness ?? (usingDefault ? 0.70 : 0.7);
  const blurPx = cfg?.blur_px ?? 0;
  // Sichtbares Hintergrundbild + edler Premium-Look: Overlay 0.50 statt 0.62
  const overlay = cfg?.overlay_opacity ?? (usingDefault ? 0.50 : 0.35);
  const filterCss = FILTER_CSS[cfg?.color_filter] || "";

  // Setze data-Attribute am <html>-Element, damit CSS-Klassen auf Pages reagieren können
  useEffect(() => {
    try {
      if (hasImage) {
        document.documentElement.setAttribute("data-has-bg", "1");
      } else {
        document.documentElement.removeAttribute("data-has-bg");
      }
      // Lesbarkeit-Slider (text_contrast 0..1) auf CSS-Variablen mappen
      const tc = Math.max(0, Math.min(1, Number(cfg?.text_contrast ?? 0.6)));
      // Chrome-Alpha 0.30 (sehr transparent) .. 0.85 (sehr dunkel)
      const chromeAlpha = (0.30 + tc * 0.55).toFixed(2);
      // Text-Shadow-Stärke 0..0.85
      const shadowAlpha = (0.20 + tc * 0.65).toFixed(2);
      // Headline-Shadow (stärker)
      const headlineShadowAlpha = (0.35 + tc * 0.55).toFixed(2);
      const root = document.documentElement.style;
      root.setProperty("--tga-chrome-alpha", chromeAlpha);
      root.setProperty("--tga-text-shadow-alpha", shadowAlpha);
      root.setProperty("--tga-headline-shadow-alpha", headlineShadowAlpha);
    } catch { /* noop */ }
  }, [hasImage, cfg?.text_contrast]);

  const bgLayer = hasImage
    ? {
        backgroundImage: `url(${effectiveImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        filter: `brightness(${brightness}) ${blurPx > 0 ? `blur(${blurPx}px)` : ""} ${filterCss}`.trim(),
        transform: blurPx > 0 ? "scale(1.05)" : undefined, // verhindert Blur-Edges
      }
    : {
        background: "linear-gradient(180deg, #0b1220 0%, #0f1b2e 100%)",
      };

  return (
    <>
      <div
        aria-hidden
        style={{
          position: "fixed",
          inset: 0,
          zIndex: -2,
          ...bgLayer,
        }}
        data-testid="app-bg-layer"
      />
      {/* Dark Overlay */}
      <div
        aria-hidden
        style={{
          position: "fixed",
          inset: 0,
          zIndex: -1,
          background: `rgba(0,0,0,${overlay})`,
          pointerEvents: "none",
        }}
        data-testid="app-bg-overlay"
      />
    </>
  );
}
